import { Injectable } from "@angular/core"
import { HttpClient } from "@angular/common/http"
import {
  BaseApiService,
  BaseCacheableApiService,
  BaseEnvironmentService,
  PaginatedResult,
  PLState
} from '@pl/pl-lib/common';

import { Coach } from "../models/coach"
import { CreateCoach } from "../models/dtos/coach/coach.create-dto"
import { UpdateCoach } from "../models/dtos/coach/coach.update-dto"
import { CoachQualification } from "../models/coachqualification"
import { from, map, Observable } from 'rxjs';
import { process, State } from '@progress/kendo-data-query';
import { CoachGender } from '../models/enums/coach-gender.enum';
import { CoachIdentity } from '../models/enums/coach-identity.enum';

@Injectable({
  providedIn: 'root'
})
export class CoachService extends BaseCacheableApiService<
  Coach,
  CreateCoach,
  UpdateCoach
> {
  constructor(http: HttpClient, environmentService: BaseEnvironmentService) {
    super('coaches', http, environmentService, ['providerId']);
  }

  public approveCoach(coachId: string) {
    return this.http.post(
      `${this.environmentService.apiHostUrl}/coaches/approve`,
      { id: coachId }
    );
  }

  override convert(input: Coach): any {
    const coreQualifications =
      input.coachQualifications?.filter((qualification) => {
        return qualification?.course?.core;
      }) || [];

    const highestCoreQualification =
      coreQualifications.length > 0
        ? coreQualifications.sort((a, b) => a.course.order - b.course.order)[0]
        : null;

    const desiredRegions =
      input.coachDesiredRegions
        ?.map((desiredRegion) => `${desiredRegion?.region?.name || ''}`)
        .filter((name) => name.length > 0) ?? [];

    const desiredDepartments =
      input.coachDesiredDepartments
        ?.map(
          (desiredDepartment) => `${desiredDepartment?.department?.name || ''}`
        )
        .filter((name) => name.length > 0) ?? [];

    const tableEducation = input.coachEducations.map((education) => `${education.field} (${education.grade})`).filter((name) => name.length > 0)?.join(', ');
    const tableExperience = input.coachExperiences.map((experience) => `${experience.role_title} (${experience.company_name})`).filter((name) => name.length > 0)?.join(', ');
    const tableQualification = input.coachQualifications.filter((qualification) => !qualification?.course?.core).map((qualification) => qualification.course.name).filter((name) => name.length > 0)?.join(', ');

    const output = {
      ...input,
      desiredRegions: desiredRegions,
      desiredDepartments: desiredDepartments,
      highestCoreQualification: highestCoreQualification?.course?.name || '',
      displayNumber: input.number
        ? `COACH${input.number.toString().padStart(5, '0')}`
        : 'COACH00000',
      // for export:
      tableGender: CoachGender[input.gender as any],
      tableEthnic_identity: CoachIdentity[input.ethnic_identity as any],
      tableDesiredRegions: desiredRegions.join(', '),
      tableDesiredDepartments: desiredDepartments.join(', '),
      tableEducation,
      tableExperience,
      tableQualification,
    };
    return output;
  }

  override getAllPaginated(
    state?: PLState
  ): Observable<PaginatedResult<Coach>> {
    // Fetch ALL from backend (no server-side paging) -> convert() -> apply state client-side -> return as PaginatedResult
    return from(super.getAllRecursive()).pipe(
      map((all) => all.map((c) => this.convert(c))),
      map((converted) => {
        const result = process(converted, (state as State) ?? {});
        return {
          data: result.data as Coach[],
          count: result.total
        } as PaginatedResult<Coach>;
      })
    );
  }

  override getAll(state?: PLState): Observable<Coach[]> {
    return from(super.getAllRecursive()).pipe(
      map((all) => all.map((c) => this.convert(c))),
      map((converted) => {
        const result = process(converted, (state as State) ?? {});
        return result.data as Coach[];
      })
    );
  }

  protected override getAllInternal(
    urlBuilder?: () => string,
    state?: PLState
  ): Observable<PaginatedResult<Coach>> {
    // bypass BaseCacheableApiService’s override
    const baseCall: (ub?: () => string, st?: PLState) => Observable<PaginatedResult<Coach>> =
      (BaseApiService as any).prototype.getAllInternal;

    return baseCall.call(this, urlBuilder, undefined).pipe(
      map((result) => {
        const converted = (result?.data ?? []).map((c) => this.convert(c));
        const processed = process(converted, (state as State) ?? {});
        return {
          ...result,
          data: processed.data as Coach[],
          count: processed.total
        } as PaginatedResult<Coach>;
      })
    );
  }
}
